/**
 * 云牛集 · 牛只交易平台 — Mock 数据与降级开关
 *
 * ★ 降级策略（架构 §9）：
 *   竞价（/auctions）、挂牌（/listings）、交易大厅聚合（/trade/hall）后端可能未实现，
 *   API 层先写真实调用，失败时自动降级到此文件 mock 数据。
 *   后端接口就绪后，仅需将下方 USE_MOCK 对应项改为 false 即可无缝切换。
 *
 *   mock 数据为「会话级内存态」，支持出价/下单/接单等操作模拟，刷新页面后重置。
 */
import type {
  Auction,
  AuctionBid,
  AuctionStatus,
  Broker,
  BrokerOrder,
  Cattle,
  CreateListingParams,
  CreateOrderResult,
  HallData,
  Listing,
  MarketPriceItem,
  NotificationItem,
  PlaceBidResult,
  PriceTrend,
  QuoteStats,
  RegionPriceItem,
  ReviewItem,
  TradeOrder,
  TransitOrder,
} from '@/types';
import { AUCTION_EXTEND_MINUTES, LOCK_MINUTES, cosUrl } from '@/utils/constants';
import { genBrokerOrderNo, genOrderNo } from '@/utils/format';

/** ★ 统一降级开关：接口就绪后把对应项改为 false（T05 已关闭 auction/listing/hall，保留 mock 函数供网络/404 降级） */
export const USE_MOCK = {
  auction: false, // 竞价接口（/auctions 系列，已接真实后端）
  listing: false, // 挂牌接口（/listings 系列，已接真实后端）
  hall: false, // 交易大厅聚合接口（/trade/hall，已接真实后端）
  market: false, // 行情接口（公开 + analytics，已实现则用真实）
  broker: false, // F3：经纪接口（/brokers、/broker-orders，已接真实后端）
};

export function mockDelay<T>(value: T, ms = 260): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), ms);
  });
}

// ===== 基础 mock 数据 =====

const nowIso = (offsetMs = 0): string => new Date(Date.now() + offsetMs).toISOString();

const farms = ['山东示范养殖场', '齐鲁牧业集团', '潍坊恒泰牧业', '临沂金穗牧场', '济宁圣源养殖场', '德州鲁旺畜牧'];
const regions = ['潍坊', '临沂', '济宁', '德州', '聊城', '滨州', '菏泽', '济南'];

/** 品种 → 默认配图（本地 public/images 路径，makeCattle 时转为 COS 公网链接） */
const BREED_IMAGE: Record<string, string> = {
  西门塔尔: '/images/cattle-simmental.jpg',
  安格斯: '/images/cattle-angus.jpg',
  夏洛莱: '/images/cattle-charolais.jpg',
  利木赞: '/images/cattle-limousin.jpg',
  本地黄牛: '/images/cattle-yellow.jpg',
  娟姗: '/images/cattle-jersey.jpg',
};

const DEFAULT_CATTLE_IMAGE = '/images/cattle-default.jpg';

function makeCattle(
  id: number,
  breed: string,
  gender: number,
  weight: number,
  ageDays: number,
  price: number,
  tradeMode: number,
  saleStatus = 0,
  availableQty = 1,
): Cattle {
  return {
    id,
    ear_tag_no: `TAG2026${`${100000 + id}`.slice(-6)}`,
    rfid_tag: `RFID${String(id).padStart(4, '0')}`,
    breed,
    gender,
    birth_date: new Date(Date.now() - ageDays * 86400000).toISOString().slice(0, 10),
    weight,
    age_days: ageDays,
    coat_color: gender === 1 ? '红白花' : '黑花',
    farm_name: farms[id % farms.length],
    health_status: '健康',
    quarantine_cert: `QZ2026${String(id).padStart(6, '0')}`,
    status: 0,
    images: [cosUrl(BREED_IMAGE[breed] ?? DEFAULT_CATTLE_IMAGE)],
    face_image_url: cosUrl(BREED_IMAGE[breed] ?? DEFAULT_CATTLE_IMAGE),
    video_url: undefined,
    sale_status: saleStatus,
    listing_price: price,
    min_bid_price: tradeMode === 2 ? price : undefined,
    bid_increment: tradeMode === 2 ? 200 : undefined,
    trade_mode: tradeMode,
    trace_score: 4 + (id % 2),
    distance_km: 18 + ((id * 13) % 220),
    available_qty: availableQty,
    seller: {
      id: 100 + (id % 5),
      real_name: `张${'甲乙丙丁戊'[id % 5]}`,
      enterprise_name: farms[id % farms.length],
      credit_score: 88 + (id % 12),
    },
  };
}

const BREEDS = ['西门塔尔', '安格斯', '夏洛莱', '利木赞', '本地黄牛', '娟姗'];

// 2026-08-03 行情校正：挂牌价（元/头）≈ 体重(kg) × 品种单价(元/kg)，标准 600-700kg 育肥牛回落至 16,000-19,500 元/头；
// 轻体重牛、犊牛/青年牛（170-190 天）价格自然更低，保持原有合理档位不变。
export const mockCattle: Cattle[] = [
  makeCattle(1, '西门塔尔', 1, 620.5, 540, 17700, 1, 0, 3),
  makeCattle(2, '安格斯', 1, 560.0, 480, 17000, 1, 0, 2),
  makeCattle(3, '西门塔尔', 0, 510.2, 620, 14500, 1, 0, 1),
  makeCattle(4, '夏洛莱', 1, 650.8, 560, 17900, 2, 0, 1),
  makeCattle(5, '本地黄牛', 1, 480.3, 520, 12700, 1, 0, 5),
  makeCattle(6, '利木赞', 1, 600.1, 500, 17400, 2, 0, 1),
  makeCattle(7, '娟姗', 0, 420.6, 460, 10100, 1, 0, 2),
  makeCattle(8, '西门塔尔', 1, 580.7, 590, 17000, 1, 0, 4),
  makeCattle(9, '安格斯', 0, 505.4, 430, 15200, 2, 0, 1),
  makeCattle(10, '夏洛莱', 1, 672.9, 610, 18500, 1, 0, 2),
  makeCattle(11, '西门塔尔', 1, 598.3, 170, 12600, 1, 0, 3),
  makeCattle(12, '利木赞', 0, 470.2, 150, 11800, 1, 0, 2),
  makeCattle(13, '本地黄牛', 0, 452.1, 380, 12000, 1, 0, 1),
  makeCattle(14, '安格斯', 1, 545.6, 520, 16400, 1, 0, 2),
  makeCattle(15, '西门塔尔', 1, 615.0, 640, 17500, 1, 0, 1),
  makeCattle(16, '娟姗', 1, 398.5, 420, 9600, 2, 0, 1),
  makeCattle(17, '西门塔尔', 1, 588.2, 190, 13200, 1, 0, 2),
  makeCattle(18, '夏洛莱', 1, 640.4, 580, 17600, 1, 1, 1),
  makeCattle(19, '安格斯', 0, 500.8, 470, 15000, 1, 3, 1),
  makeCattle(20, '西门塔尔', 1, 605.3, 560, 17300, 1, 0, 3),
];

// ===== 挂牌 mock =====
export const mockListings: Listing[] = mockCattle
  .filter((c) => c.sale_status !== undefined)
  .map((c, idx) => ({
    id: idx + 1,
    cattle_id: c.id,
    cattle: c,
    trade_mode: c.trade_mode ?? 1,
    price: c.listing_price,
    bid_increment: c.bid_increment,
    auction_start: c.trade_mode === 2 ? nowIso(-86400000 * 2) : undefined,
    auction_end: c.trade_mode === 2 ? nowIso(86400000 * 3) : undefined,
    available_qty: c.available_qty ?? 1,
    status: c.sale_status === 4 ? 0 : c.sale_status === 1 ? 1 : c.sale_status === 3 ? 2 : 1,
    created_at: nowIso(-86400000 * (idx % 6)),
  }));

// ===== 竞价 mock（会话级可变）=====
// 2026-08-03 行情校正：起拍/当前价按 体重×品种单价 回落至合理区间（标准 600-700kg 育肥牛 16,000-19,500 元/头），
// 出价记录保持逐步加价、成交价 > 起拍价 的递增逻辑不变。
export const mockAuctions: Auction[] = [
  {
    id: 101,
    cattle_id: 4,
    cattle: mockCattle.find((c) => c.id === 4),
    mode: 1,
    base_price: 17000,
    current_price: 18000,
    bid_increment: 200,
    status: 1,
    start_time: nowIso(-3600000),
    end_time: nowIso(9 * 60000),
    winner_id: null,
    winner_price: null,
    bid_count: 6,
    bids: [
      { id: 9001, auction_id: 101, user_id: 3, user_name: '王**', price: 17000, created_at: nowIso(-3600000) },
      { id: 9002, auction_id: 101, user_id: 5, user_name: '李**', price: 17200, created_at: nowIso(-3300000) },
      { id: 9003, auction_id: 101, user_id: 7, user_name: '赵**', price: 17400, created_at: nowIso(-3000000) },
      { id: 9004, auction_id: 101, user_id: 3, user_name: '王**', price: 17600, created_at: nowIso(-2400000) },
      { id: 9005, auction_id: 101, user_id: 5, user_name: '李**', price: 17800, created_at: nowIso(-1500000) },
      { id: 9006, auction_id: 101, user_id: 9, user_name: '周**', price: 18000, created_at: nowIso(-600000) },
    ],
  },
  {
    id: 102,
    cattle_id: 6,
    cattle: mockCattle.find((c) => c.id === 6),
    mode: 1,
    base_price: 17400,
    current_price: 17400,
    bid_increment: 200,
    status: 1,
    start_time: nowIso(-1200000),
    end_time: nowIso(14 * 60000),
    winner_id: null,
    winner_price: null,
    bid_count: 0,
    bids: [],
  },
  {
    id: 103,
    cattle_id: 9,
    cattle: mockCattle.find((c) => c.id === 9),
    mode: 1,
    base_price: 15200,
    current_price: 15200,
    bid_increment: 200,
    status: 0,
    start_time: nowIso(86400000),
    end_time: nowIso(86400000 + 3600000),
    winner_id: null,
    winner_price: null,
    bid_count: 0,
    bids: [],
  },
  {
    id: 104,
    cattle_id: 16,
    cattle: mockCattle.find((c) => c.id === 16),
    mode: 1,
    base_price: 9600,
    current_price: 9600,
    bid_increment: 100,
    status: 0,
    start_time: nowIso(86400000 * 2),
    end_time: nowIso(86400000 * 2 + 3600000),
    winner_id: null,
    winner_price: null,
    bid_count: 0,
    bids: [],
  },
  {
    id: 105,
    cattle_id: 2,
    cattle: mockCattle.find((c) => c.id === 2),
    mode: 1,
    base_price: 17000,
    current_price: 18100,
    bid_increment: 100,
    status: 2,
    start_time: nowIso(-86400000 * 2),
    end_time: nowIso(-86400000 + 3600000),
    winner_id: 5,
    winner_price: 18100,
    bid_count: 12,
    // 12 条递增出价：17000 → 18100，步长 100（=bid_increment），成交价 > 起拍价，与 bid_count 自洽
    bids: [
      { id: 9101, auction_id: 105, user_id: 3, user_name: '王**', price: 17000, created_at: nowIso(-86400000 * 2) },
      { id: 9102, auction_id: 105, user_id: 5, user_name: '李**', price: 17100, created_at: nowIso(-86400000 * 2 + 7200000) },
      { id: 9103, auction_id: 105, user_id: 7, user_name: '赵**', price: 17200, created_at: nowIso(-86400000 * 2 + 14400000) },
      { id: 9104, auction_id: 105, user_id: 9, user_name: '周**', price: 17300, created_at: nowIso(-86400000 * 2 + 21600000) },
      { id: 9105, auction_id: 105, user_id: 3, user_name: '王**', price: 17400, created_at: nowIso(-86400000 * 2 + 28800000) },
      { id: 9106, auction_id: 105, user_id: 5, user_name: '李**', price: 17500, created_at: nowIso(-86400000 * 2 + 36000000) },
      { id: 9107, auction_id: 105, user_id: 7, user_name: '赵**', price: 17600, created_at: nowIso(-86400000 * 2 + 43200000) },
      { id: 9108, auction_id: 105, user_id: 9, user_name: '周**', price: 17700, created_at: nowIso(-86400000 * 2 + 50400000) },
      { id: 9109, auction_id: 105, user_id: 3, user_name: '王**', price: 17800, created_at: nowIso(-86400000 * 2 + 57600000) },
      { id: 9110, auction_id: 105, user_id: 5, user_name: '李**', price: 17900, created_at: nowIso(-86400000 * 2 + 64800000) },
      { id: 9111, auction_id: 105, user_id: 7, user_name: '赵**', price: 18000, created_at: nowIso(-86400000 * 2 + 72000000) },
      { id: 9112, auction_id: 105, user_id: 5, user_name: '李**', price: 18100, created_at: nowIso(-86400000 + 1800000) },
    ],
  },
];

// ===== 订单 mock（会话级可变）=====
// 2026-08-03 行情校正：一口价/订单按头价回落至合理区间（标准 600-700kg 育肥牛 17,000-19,000 元/头），
// total_amount/service_fee(1.5%)/paid_amount/unit_price/subtotal 同步更新。
export const mockOrders: TradeOrder[] = [
  {
    id: 5001,
    order_no: genOrderNo(),
    order_type: 1,
    buyer_id: 3,
    seller_id: 101,
    total_amount: 17700,
    service_fee: 265.5,
    paid_amount: 17965.5,
    payment_method: 1,
    payment_status: 1,
    order_status: 1,
    delivery_method: 1,
    delivery_address: '山东省潍坊市寒亭区交割中心',
    delivery_time: nowIso(86400000),
    contact_name: '王买买',
    contact_phone: '13800001111',
    created_at: nowIso(-600000),
    expire_at: null,
    paid_at: nowIso(-300000),
    items: [{ cattle_id: 1, unit_price: 17700, quantity: 1, subtotal: 17700, cattle: mockCattle[0] }],
    buyer: { id: 3, username: 'buyer', real_name: '王买买', user_type: 3, status: 1, roles: [], permissions: [] },
    seller: { id: 101, username: 'seller', real_name: '张甲', enterprise_name: '山东示范养殖场', user_type: 3, status: 1, roles: [], permissions: [] },
  },
  {
    id: 5002,
    order_no: genOrderNo(),
    order_type: 1,
    buyer_id: 3,
    seller_id: 102,
    total_amount: 17000,
    service_fee: 255,
    paid_amount: 17255,
    payment_method: 1,
    payment_status: 1,
    order_status: 0,
    delivery_method: 2,
    delivery_address: '山东省济南市历城区',
    delivery_time: nowIso(86400 * 2),
    contact_name: '王买买',
    contact_phone: '13800001111',
    created_at: nowIso(-420000),
    expire_at: nowIso(LOCK_MINUTES * 60000 - 420000),
    items: [{ cattle_id: 2, unit_price: 17000, quantity: 1, subtotal: 17000, cattle: mockCattle[1] }],
    buyer: { id: 3, username: 'buyer', real_name: '王买买', user_type: 3, status: 1, roles: [], permissions: [] },
    seller: { id: 102, username: 'seller', real_name: '李乙', enterprise_name: '齐鲁牧业集团', user_type: 3, status: 1, roles: [], permissions: [] },
  },
  {
    id: 5003,
    order_no: genOrderNo(),
    order_type: 2,
    buyer_id: 3,
    seller_id: 104,
    total_amount: 18000,
    service_fee: 270,
    paid_amount: 18270,
    payment_method: 2,
    payment_status: 1,
    order_status: 2,
    delivery_method: 1,
    delivery_address: '山东省潍坊市奎文区',
    delivery_time: nowIso(86400 * 3),
    contact_name: '王买买',
    contact_phone: '13800001111',
    created_at: nowIso(-86400 * 2),
    expire_at: null,
    paid_at: nowIso(-86400 * 2 + 600000),
    items: [{ cattle_id: 4, unit_price: 18000, quantity: 1, subtotal: 18000, cattle: mockCattle[3] }],
    buyer: { id: 3, username: 'buyer', real_name: '王买买', user_type: 3, status: 1, roles: [], permissions: [] },
    seller: { id: 104, username: 'seller', real_name: '丁丁', enterprise_name: '潍坊恒泰牧业', user_type: 3, status: 1, roles: [], permissions: [] },
  },
  {
    id: 5004,
    order_no: genOrderNo(),
    order_type: 1,
    buyer_id: 3,
    seller_id: 105,
    total_amount: 12700,
    service_fee: 190.5,
    paid_amount: 12890.5,
    payment_method: 1,
    payment_status: 1,
    order_status: 3,
    delivery_method: 1,
    delivery_address: '山东省临沂市兰山区',
    delivery_time: nowIso(-86400),
    contact_name: '王买买',
    contact_phone: '13800001111',
    created_at: nowIso(-86400 * 5),
    expire_at: null,
    paid_at: nowIso(-86400 * 4),
    delivered_at: nowIso(-86400),
    items: [{ cattle_id: 5, unit_price: 12700, quantity: 1, subtotal: 12700, cattle: mockCattle[4] }],
    buyer: { id: 3, username: 'buyer', real_name: '王买买', user_type: 3, status: 1, roles: [], permissions: [] },
    seller: { id: 105, username: 'seller', real_name: '戊戊', enterprise_name: '临沂金穗牧场', user_type: 3, status: 1, roles: [], permissions: [] },
  },
  {
    id: 5005,
    order_no: genOrderNo(),
    order_type: 1,
    buyer_id: 3,
    seller_id: 106,
    total_amount: 17000,
    service_fee: 255,
    paid_amount: 17255,
    payment_method: 1,
    payment_status: 1,
    order_status: 4,
    delivery_method: 2,
    delivery_address: '山东省济宁市任城区',
    delivery_time: nowIso(-86400 * 2),
    contact_name: '王买买',
    contact_phone: '13800001111',
    created_at: nowIso(-86400 * 12),
    expire_at: null,
    paid_at: nowIso(-86400 * 11),
    delivered_at: nowIso(-86400 * 3),
    completed_at: nowIso(-86400 * 2),
    items: [{ cattle_id: 8, unit_price: 17000, quantity: 1, subtotal: 17000, cattle: mockCattle[7] }],
    buyer: { id: 3, username: 'buyer', real_name: '王买买', user_type: 3, status: 1, roles: [], permissions: [] },
    seller: { id: 106, username: 'seller', real_name: '己己', enterprise_name: '济宁圣源养殖场', user_type: 3, status: 1, roles: [], permissions: [] },
  },
  {
    id: 5006,
    order_no: genOrderNo(),
    order_type: 1,
    buyer_id: 3,
    seller_id: 107,
    total_amount: 16400,
    service_fee: 246,
    paid_amount: 16646,
    payment_method: 1,
    payment_status: 1,
    order_status: 6,
    delivery_method: 1,
    delivery_address: '山东省德州市德城区',
    delivery_time: nowIso(-86400 * 4),
    contact_name: '王买买',
    contact_phone: '13800001111',
    created_at: nowIso(-86400 * 6),
    expire_at: null,
    paid_at: nowIso(-86400 * 5),
    delivered_at: nowIso(-86400 * 4),
    arbitrating_at: nowIso(-86400),
    items: [{ cattle_id: 14, unit_price: 16400, quantity: 1, subtotal: 16400, cattle: mockCattle[13] }],
    buyer: { id: 3, username: 'buyer', real_name: '王买买', user_type: 3, status: 1, roles: [], permissions: [] },
    seller: { id: 107, username: 'seller', real_name: '庚庚', enterprise_name: '德州鲁旺畜牧', user_type: 3, status: 1, roles: [], permissions: [] },
  },
];

// ===== 经纪 mock =====
export let mockBroker: Broker | null = null;

export const mockBrokerOrders: BrokerOrder[] = [
  {
    id: 7001,
    order_no: genBrokerOrderNo(),
    order_type: 1,
    cattle_type: '西门塔尔',
    quantity: 60,
    price_min: 17000,
    price_max: 18500,
    commission_rate: 0.02,
    note: '育肥出栏牛，体重550kg以上',
    status: 0,
    publisher_id: 200,
    publisher_name: '刘老板',
    broker_id: null,
    expired_at: nowIso(86400 * 3),
    created_at: nowIso(-3600000),
    linked_order_id: null,
  },
  {
    id: 7002,
    order_no: genBrokerOrderNo(),
    order_type: 2,
    cattle_type: '安格斯',
    quantity: 80,
    price_min: 16000,
    price_max: 17500,
    commission_rate: 0.02,
    note: '批量出栏，可分批交割',
    status: 0,
    publisher_id: 201,
    publisher_name: '孙牧场',
    broker_id: null,
    expired_at: nowIso(86400 * 4),
    created_at: nowIso(-7200000),
    linked_order_id: null,
  },
  {
    id: 7003,
    order_no: genBrokerOrderNo(),
    order_type: 1,
    cattle_type: '夏洛莱',
    quantity: 40,
    price_min: 17500,
    price_max: 19000,
    commission_rate: 0.02,
    note: '',
    status: 1,
    publisher_id: 202,
    publisher_name: '周氏集团',
    broker_id: 300,
    broker_name: '陈经纪',
    expired_at: nowIso(86400 * 2),
    created_at: nowIso(-86400),
    linked_order_id: null,
  },
  {
    id: 7004,
    order_no: genBrokerOrderNo(),
    order_type: 2,
    cattle_type: '西门塔尔',
    quantity: 100,
    price_min: 17000,
    price_max: 18000,
    commission_rate: 0.02,
    note: '需求量大，长期合作',
    status: 3,
    publisher_id: 203,
    publisher_name: '吴商贸',
    broker_id: null,
    expired_at: null,
    created_at: nowIso(-86400 * 3),
    linked_order_id: null,
  },
  {
    id: 7005,
    order_no: genBrokerOrderNo(),
    order_type: 1,
    cattle_type: '本地黄牛',
    quantity: 30,
    price_min: 12500,
    price_max: 13500,
    commission_rate: 0.02,
    note: '',
    status: 2,
    publisher_id: 204,
    publisher_name: '郑收购',
    broker_id: 300,
    broker_name: '陈经纪',
    expired_at: null,
    created_at: nowIso(-86400 * 5),
    linked_order_id: 5099,
  },
];

// ===== 行情 mock =====
/** 品种基准单价（元/kg），与公开行情接口 2026-08-03 数据对齐 */
const BREED_BASE_PRICE: Record<string, number> = {
  西门塔尔: 28.5,
  安格斯: 30.0,
  夏洛莱: 27.5,
  利木赞: 29.0,
  本地黄牛: 26.5,
  娟姗: 24.0,
};

export const mockMarketPrices: MarketPriceItem[] = BREEDS.map((breed) => ({
  breed,
  price: BREED_BASE_PRICE[breed] ?? 28.0,
  unit: '元/kg',
  date: new Date().toISOString().slice(0, 10),
}));

export function mockPriceTrend(breed: string, days: number): PriceTrend {
  const base = BREED_BASE_PRICE[breed] ?? 28.0;
  const points = Array.from({ length: days }, (_, i) => {
    const date = new Date(Date.now() - (days - 1 - i) * 86400000);
    const wave = Math.sin(i / 4) * 1.2 + (i % 7) * 0.15;
    return {
      date: date.toISOString().slice(0, 10),
      price: Number((base + wave).toFixed(2)),
    };
  });
  const current = points[points.length - 1].price;
  const prev = points[0].price;
  return {
    breed,
    current,
    change_pct: Number((((current - prev) / prev) * 100).toFixed(2)),
    points,
  };
}

export const mockRegionPrices: RegionPriceItem[] = regions.map((city, i) => ({
  city,
  breed: BREEDS[i % BREEDS.length],
  price: 24 + (i % 5) * 1.0 + 0.5,
  unit: '元/kg',
}));

export const mockQuoteStats: QuoteStats = {
  today_avg: 27.6,
  change_pct: 2.1,
  week_volume: 1286,
  max_breed: '安格斯',
  max_price: 30.0,
  min_breed: '娟姗',
  min_price: 24.0,
};

// ===== 交易大厅聚合 =====
export const mockHall: HallData = {
  categories: [
    { key: '育肥牛', name: '育肥牛', desc: '出栏在即 · 体重达标', count: 128 },
    { key: '种牛', name: '种牛', desc: '优质种源 · 血统清晰', count: 36 },
    { key: '母牛', name: '母牛', desc: '能繁母牛 · 产犊稳定', count: 58 },
    { key: '犊牛', name: '犊牛', desc: '健康犊牛 · 检疫合格', count: 92 },
  ],
  featured: mockCattle.slice(0, 8).map((c) => ({ ...c })),
  recent_trades: [
    { id: 1, region: '潍坊', breed: '西门塔尔', quantity: 3, price: 17700, time: nowIso(-3600000 * 3) },
    { id: 2, region: '临沂', breed: '安格斯', quantity: 5, price: 17000, time: nowIso(-3600000 * 5) },
    { id: 3, region: '济宁', breed: '夏洛莱', quantity: 2, price: 17600, time: nowIso(-3600000 * 8) },
    { id: 4, region: '德州', breed: '本地黄牛', quantity: 8, price: 12700, time: nowIso(-3600000 * 12) },
    { id: 5, region: '聊城', breed: '西门塔尔', quantity: 4, price: 17300, time: nowIso(-3600000 * 18) },
    { id: 6, region: '滨州', breed: '利木赞', quantity: 3, price: 17400, time: nowIso(-3600000 * 26) },
  ],
  notices: [
    { id: 1, title: '平台公告：交割凭证 GPS 校验功能上线', content: '交割确认需上传不少于 3 张现场凭证并携带 GPS 信息。', created_at: nowIso(-86400000) },
    { id: 2, title: '关于调整散户服务费优惠的通知', content: '散户用户（个人）参与一口价交易享受免平台服务费优惠。', created_at: nowIso(-86400000 * 3) },
    { id: 3, title: '区块链存证服务升级公告', content: '合同与交割凭证将同步上链存证，支持公开查验。', created_at: nowIso(-86400000 * 6) },
  ],
  total_on_sale: 314,
};

// ===== 通知 mock =====
export const mockNotifications: NotificationItem[] = [
  { id: 1, type: 'order', title: '订单待付款', content: '您的订单 ORD-... 已生成，请在 15 分钟内完成支付。', read: false, created_at: nowIso(-600000) },
  { id: 2, type: 'auction', title: '出价被超越', content: '您在竞价 101 中的出价已被超越，请及时跟进。', read: false, created_at: nowIso(-1800000) },
  { id: 3, type: 'system', title: '交割凭证提醒', content: '订单 5004 待确认交割，请上传不少于 3 张凭证。', read: true, created_at: nowIso(-86400000) },
];

// ===== 评价 mock =====
export const mockReviews: ReviewItem[] = [
  { id: 1, order_id: 5005, reviewer_id: 3, reviewee_id: 2, role: 1, rating: 5, content: '牛只健康，重量与挂牌一致，交割流程顺畅。', is_auto: false, reviewer_name: '王买买', created_at: nowIso(-86400) },
];

// ===== 溯源 mock =====
export function mockTraceEvents(cattleId: number) {
  const born = new Date(Date.now() - 500 * 86400000);
  return [
    { id: 1, cattle_id: cattleId, event_type: 1, event_desc: '出生建档', location: '山东潍坊', operator: '养殖场', event_time: born.toISOString(), tx_hash: `0x${'a'.repeat(10)}${cattleId}` },
    { id: 2, cattle_id: cattleId, event_type: 2, event_desc: '首次免疫（口蹄疫）', location: '山东潍坊', operator: '兽医站', event_time: new Date(born.getTime() + 30 * 86400000).toISOString(), tx_hash: `0x${'b'.repeat(10)}${cattleId}` },
    { id: 3, cattle_id: cattleId, event_type: 3, event_desc: '定期检疫合格', location: '山东潍坊', operator: '检疫站', event_time: new Date(born.getTime() + 200 * 86400000).toISOString(), tx_hash: `0x${'c'.repeat(10)}${cattleId}` },
    { id: 4, cattle_id: cattleId, event_type: 4, event_desc: '出栏转运', location: '山东潍坊', operator: '平台物流', event_time: new Date(Date.now() - 20 * 86400000).toISOString(), tx_hash: `0x${'d'.repeat(10)}${cattleId}` },
    { id: 5, cattle_id: cattleId, event_type: 5, event_desc: '上架交易', location: '云牛集交易平台', operator: '平台', event_time: nowIso(-3 * 86400000), tx_hash: `0x${'e'.repeat(10)}${cattleId}` },
  ];
}

// ===== 在途 mock（部分订单有）=====
export function mockTransitByOrder(orderId: number): TransitOrder | null {
  if (orderId === 5003) {
    return {
      id: 8001,
      transit_no: 'TRN202607180001',
      order_id: 5003,
      cattle_id: 4,
      status: 1,
      origin_city: '潍坊',
      destination_city: '济南',
      plate_no: '鲁A12345',
      driver_name: '孙师傅',
      driver_phone: '13900002222',
      depart_at: nowIso(-86400),
      arrive_at: null,
      gps_track: [
        { lng: 119.1, lat: 36.62, time: nowIso(-86400) },
        { lng: 118.9, lat: 36.55, time: nowIso(-43200) },
        { lng: 118.4, lat: 36.4, time: nowIso(-6000) },
      ],
    };
  }
  return null;
}

export function mockTransitByCattle(cattleId: number): TransitOrder | null {
  if (cattleId === 4) return mockTransitByOrder(5003);
  return null;
}

// ===== Mock 竞价函数（含防秒杀延长 IR-02）=====
function findAuction(id: number): Auction {
  const a = mockAuctions.find((x) => x.id === id);
  if (!a) throw new Error('竞价不存在');
  return a;
}

export function mockListAuctions(status?: number): Promise<Auction[]> {
  const list = status === undefined ? mockAuctions : mockAuctions.filter((a) => a.status === status);
  return mockDelay(list.map((a) => ({ ...a, cattle: a.cattle, bids: undefined })));
}

export function mockGetAuction(id: number): Promise<Auction> {
  const a = findAuction(id);
  return mockDelay({
    ...a,
    cattle: a.cattle,
    bids: a.bids ? [...a.bids].sort((x, y) => y.price - x.price) : [],
  });
}

export function mockPlaceBid(auctionId: number, price: number, userId: number): Promise<PlaceBidResult> {
  const a = findAuction(auctionId);
  if (a.status !== 1) throw new Error('竞价已结束');
  if (price < a.current_price + a.bid_increment) {
    throw new Error('出价必须 ≥ 当前价 + 最小加价幅度');
  }
  const bid: AuctionBid = {
    id: Date.now(),
    auction_id: auctionId,
    user_id: userId,
    user_name: `用户${userId}**`,
    price,
    created_at: nowIso(),
  };
  a.bids = a.bids ? [...a.bids, bid] : [bid];
  a.current_price = price;
  a.bid_count += 1;

  // 防秒杀：最后 1 分钟内有新出价 → 延长 2 分钟（IR-02）
  const remaining = new Date(a.end_time).getTime() - Date.now();
  let extended = false;
  if (remaining > 0 && remaining < 60000) {
    a.end_time = nowIso(AUCTION_EXTEND_MINUTES * 60000);
    extended = true;
  }
  return mockDelay(
    {
      bid,
      current_price: a.current_price,
      end_time: a.end_time,
      extended,
      my_bid_status: 'leading',
    },
    300,
  );
}

export function mockAuctionStatus(id: number, myUserId?: number): Promise<AuctionStatus> {
  const a = findAuction(id);
  let myBidStatus: 'leading' | 'outbid' | null = null;
  if (myUserId !== undefined && a.bids && a.bids.length > 0) {
    const myBids = a.bids.filter((b) => b.user_id === myUserId);
    const highest = a.bids.reduce((m, b) => (b.price > m.price ? b : m), a.bids[0]);
    if (myBids.length > 0) {
      myBidStatus = highest.user_id === myUserId ? 'leading' : 'outbid';
    }
  }
  return mockDelay({
    id: a.id,
    current_price: a.current_price,
    end_time: a.end_time,
    bid_count: a.bid_count,
    status: a.status,
    my_bid_status: myBidStatus,
    last_bid: a.bids && a.bids.length > 0 ? a.bids[a.bids.length - 1] : null,
  });
}

// ===== Mock 挂牌函数 =====
export function mockCreateListing(params: CreateListingParams): Promise<{ id: number; status: number }> {
  const id = mockListings.length + 1;
  const first = mockCattle.find((c) => params.cattle_ids.includes(c.id));
  if (!first) throw new Error('请选择牛只');
  const listing: Listing = {
    id,
    cattle_id: first.id,
    cattle: first,
    trade_mode: params.trade_mode,
    price: params.price,
    bid_increment: params.bid_increment,
    auction_start: params.auction_start,
    auction_end: params.auction_end,
    available_qty: params.available_qty,
    status: 0, // 待审核 → 模拟 1 秒后自动上架（IR-16）
    created_at: nowIso(),
  };
  mockListings.unshift(listing);
  const cattle = mockCattle.find((c) => c.id === first.id);
  if (cattle) cattle.sale_status = 4;
  window.setTimeout(() => {
    listing.status = 1;
    const c = mockCattle.find((x) => x.id === first.id);
    if (c) c.sale_status = 0;
  }, 1200);
  return mockDelay({ id, status: 0 });
}

export function mockListMyListings(): Promise<Listing[]> {
  return mockDelay([...mockListings]);
}

export function mockUpdateListingStatus(id: number, status: number): Promise<{ ok: boolean }> {
  const l = mockListings.find((x) => x.id === id);
  if (l) {
    l.status = status;
    const c = l.cattle ? mockCattle.find((x) => x.id === l.cattle_id) : undefined;
    if (c) c.sale_status = status === 1 ? 0 : 3;
  }
  return mockDelay({ ok: true });
}

// ===== Mock 订单函数 =====
export function mockCreateOrder(payload: {
  order_type: number;
  seller_id: number;
  items: { cattle_id: number; unit_price: number; quantity: number }[];
  payment_method: number;
  delivery_method?: number;
  delivery_address?: string;
  delivery_time?: string;
  contact_name?: string;
  contact_phone?: string;
  remark?: string;
  auction_id?: number;
  deal_price?: number;
}): Promise<CreateOrderResult> {
  const total = payload.items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  // mock 对齐后端统一口径：拍卖 2.0% 无最低；一口价 1.5% 最低 50
  const serviceFee =
    payload.order_type === 3 ? total * 0.02 : total * 0.015 < 50 ? 50 : total * 0.015;
  const order: TradeOrder = {
    id: 6000 + mockOrders.length,
    order_no: genOrderNo(),
    order_type: payload.order_type,
    buyer_id: 3,
    seller_id: payload.seller_id,
    total_amount: total,
    service_fee: serviceFee,
    paid_amount: total + serviceFee,
    payment_method: payload.payment_method,
    payment_status: 0,
    order_status: 0,
    delivery_method: payload.delivery_method,
    delivery_address: payload.delivery_address,
    delivery_time: payload.delivery_time,
    contact_name: payload.contact_name,
    contact_phone: payload.contact_phone,
    created_at: nowIso(),
    expire_at: nowIso(LOCK_MINUTES * 60000),
    auction_id: payload.auction_id,
    deal_price: payload.deal_price,
    items: payload.items.map((i) => ({
      cattle_id: i.cattle_id,
      unit_price: i.unit_price,
      quantity: i.quantity,
      subtotal: i.unit_price * i.quantity,
      cattle: mockCattle.find((c) => c.id === i.cattle_id),
    })),
    buyer: { id: 3, username: 'buyer', real_name: '王买买', user_type: 3, status: 1, roles: [], permissions: [] },
    seller: { id: payload.seller_id, username: 'seller', real_name: '卖家', user_type: 3, status: 1, roles: [], permissions: [] },
  };
  mockOrders.unshift(order);
  const cattle = mockCattle.find((c) => c.id === payload.items[0].cattle_id);
  if (cattle) cattle.sale_status = 1;
  return mockDelay({
    id: order.id,
    order_id: order.id,
    order_no: order.order_no,
    total_amount: order.total_amount,
    expire_at: order.expire_at ?? null,
    service_fee: order.service_fee,
  });
}

export function mockListOrders(): Promise<TradeOrder[]> {
  return mockDelay([...mockOrders]);
}

export function mockGetOrder(id: number): Promise<TradeOrder> {
  const o = mockOrders.find((x) => x.id === id);
  if (!o) throw new Error('订单不存在');
  return mockDelay({ ...o, transit: mockTransitByOrder(id) });
}

export function mockPayOrder(id: number): Promise<{ ok: boolean }> {
  const o = mockOrders.find((x) => x.id === id);
  if (o) {
    o.payment_status = 1;
    // F1：支付成功后直接进入「待交割」(2) —— 买家即可上传 ≥3 张凭证 + GPS 确认交割
    o.order_status = 2;
    o.paid_at = nowIso();
    o.expire_at = null;
    const c = o.items?.[0] ? mockCattle.find((x) => x.id === o.items![0].cattle_id) : undefined;
    if (c) c.sale_status = 1;
  }
  return mockDelay({ ok: true });
}

/** F1：卖家「安排交割」→ 已付款(1) → 待交割(2) */
export function mockShipOrder(id: number): Promise<{ ok: boolean }> {
  const o = mockOrders.find((x) => x.id === id);
  if (o && o.order_status === 1) {
    o.order_status = 2;
  }
  return mockDelay({ ok: true });
}

export function mockCancelOrder(id: number, reason?: string): Promise<{ ok: boolean }> {
  const o = mockOrders.find((x) => x.id === id);
  if (o) {
    o.order_status = 5;
    o.cancelled_at = nowIso();
    o.cancel_reason = reason || '买家取消';
    const c = o.items?.[0] ? mockCattle.find((x) => x.id === o.items![0].cattle_id) : undefined;
    if (c) c.sale_status = 0;
  }
  return mockDelay({ ok: true });
}

export function mockCompleteOrder(id: number, vouchers: string[]): Promise<{ ok: boolean }> {
  const o = mockOrders.find((x) => x.id === id);
  if (o) {
    o.order_status = 3;
    o.delivered_at = nowIso();
    o.delivery_vouchers = vouchers;
  }
  return mockDelay({ ok: true });
}

export function mockApplyArbitration(id: number): Promise<{ ok: boolean }> {
  const o = mockOrders.find((x) => x.id === id);
  if (o) {
    o.order_status = 6;
    o.arbitrating_at = nowIso();
  }
  return mockDelay({ ok: true });
}

// ===== Mock 经纪函数 =====
export function mockApplyBroker(params: {
  real_name: string;
  phone: string;
  cert_no: string;
  license_no?: string;
}): Promise<Broker> {
  const broker: Broker = {
    id: 900,
    user_id: 3,
    real_name: params.real_name,
    phone: params.phone,
    cert_no: params.cert_no || params.license_no,
    status: 0, // 待审核（3101 经纪资质未审核通过文案由页面处理）
    rating: 0,
  };
  mockBroker = broker;
  return mockDelay(broker);
}

export function mockGetMyBroker(): Promise<Broker | null> {
  return mockDelay(mockBroker);
}

export function mockPublishBrokerOrder(params: {
  type: number;
  breed?: string;
  quantity: number;
  price_min?: number;
  price_max?: number;
  commission_rate?: number;
  remark?: string;
}): Promise<{ id: number }> {
  const order: BrokerOrder = {
    id: 8000 + mockBrokerOrders.length,
    order_no: genBrokerOrderNo(),
    order_type: params.type,
    cattle_type: params.breed,
    quantity: params.quantity,
    price_min: params.price_min,
    price_max: params.price_max,
    commission_rate: params.commission_rate,
    note: params.remark,
    status: 0,
    publisher_id: 3,
    publisher_name: '王买买',
    broker_id: null,
    expired_at: nowIso(86400 * 3),
    created_at: nowIso(),
    linked_order_id: null,
  };
  mockBrokerOrders.unshift(order);
  return mockDelay({ id: order.id });
}

export function mockListBrokerOrders(view?: 'mine' | 'available' | 'matched' | 'cancelled' | 'expired'): Promise<BrokerOrder[]> {
  let list = [...mockBrokerOrders];
  if (view === 'mine') list = list.filter((o) => o.publisher_id === 3 || o.broker_id === 300);
  if (view === 'available') list = list.filter((o) => o.status === 0);
  if (view === 'matched') list = list.filter((o) => o.status === 1 || o.status === 2);
  if (view === 'cancelled') list = list.filter((o) => o.status === 3);
  if (view === 'expired') list = list.filter((o) => o.status === 4);
  return mockDelay(list);
}

export function mockAcceptBrokerOrder(id: number): Promise<{ ok: boolean }> {
  const o = mockBrokerOrders.find((x) => x.id === id);
  if (o) {
    o.status = 1;
    o.broker_id = 300;
    o.broker_name = '陈经纪';
  }
  return mockDelay({ ok: true });
}

export function mockMatchBrokerOrder(id: number): Promise<{ linked_order_id: number }> {
  const o = mockBrokerOrders.find((x) => x.id === id);
  const linkedId = 9000 + id;
  if (o) {
    o.status = 2;
    o.linked_order_id = linkedId;
  }
  return mockDelay({ linked_order_id: linkedId });
}

export function mockCancelBrokerOrder(id: number): Promise<{ ok: boolean }> {
  const o = mockBrokerOrders.find((x) => x.id === id);
  if (o) o.status = 3;
  return mockDelay({ ok: true });
}
